import { makeAutoObservable } from 'mobx';
import { Airplane } from './airplane';
import { Airport } from './airport';
import { AIRPORT_LIST, AirportSlug } from './data/airportData';
import { checkManyMoney, LOGGER, uuid } from './helpers';
import { POPUP } from './popupManager';
import { Manager } from './manager';
import { CompetitionTripData, SaveData, TutorialStep } from './data.type';
import { REWARD } from './rewardedManager';
import { Lounge } from './lounge';
import { CrazySDK } from '../crazySDK';
import { AIRPLANE_DATA } from './data/airplaneData';
import { MAX_OFFLINE_HOURS, OFFLINE_INCOME } from './offlineIncome';
import { ADVANCE_COEFFICIENT, ADVANCE_PASSENGERS_COEFFICIENT as ADVANCE_PASSENGERS_COEFFICIENT } from './data/baseData';
import { subHours } from 'date-fns';
import { setAdMute, setAudioMute } from './audio';

const DATA_KEY = 'airline_tycoon_game_data';

class GameData {
  // save data
  airports: Airport[] = [];
  airplanes: Airplane[] = [];
  managers: Manager[] = [];
  lounges: Lounge[] = [];
  money: number = 0;
  transportedPassangers: number = 0;
  advanceLevel: number = 0;
  tutorialStep: TutorialStep = 'step1';
  isMuted: boolean = false;

  // transient data
  openPopup: 'advance' | null = null;
  sidebarSection: 'airplanes' | 'airports' | 'managers' | 'lounges' = 'airplanes';
  popupData: {} = {};
  sidebarData: { highlightedAirportSlug?: AirportSlug } = {};
  planTripPopup: AirportSlug | null = null;
  competitionAirplanes: CompetitionTripData[] = [];
  isVideoPlaying: boolean = false;
  buyMode: 'buy-1' | 'buy-max' = 'buy-1';

  pauseManager: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  loadDefaultData() {
    this.airports.forEach((a) => a.dispose());
    this.airplanes.forEach((a) => a.dispose());
    this.managers.forEach((m) => m.dispose());
    this.competitionAirplanes.forEach((c) => window.clearTimeout(c.timeoutId));
    this.competitionAirplanes = [];
    this.managers = [];
    this.airports = [];
    this.airplanes = [];
    this.lounges = [];
    AIRPORT_LIST.slice().forEach((airportData, index) => {
      this.airports.push(new Airport(index, airportData.name, airportData.slug, airportData.pos));
    });
    for (let i = 0; i < AIRPLANE_DATA.DEFAULT_AIRPLANES; i++) {
      this.addAirplane(new Airplane(i, 'Airplane ' + (i + 1), this.randomAvailableAirport().slug, 0));
    }
    this.sortStuff();
  }

  load() {
    if (window.location.href.includes('reset_data=true')) {
      CrazySDK.data.removeItem(DATA_KEY);
    }

    const existingDataStr = CrazySDK.data.getItem(DATA_KEY);
    LOGGER.log('Existing data', existingDataStr);
    let existingData: SaveData | null = null;
    if (existingDataStr) {
      try {
        existingData = JSON.parse(existingDataStr);
      } catch (e) {
        LOGGER.error('Failed to parse existing data, game will init with new data', e);
      }
    }

    if (existingData !== null) {
      LOGGER.log('Loading existing data');
      this.money = existingData.money;
      this.transportedPassangers = existingData.transportedPassangers;
      this.advanceLevel = existingData.advanceLevel;
      this.tutorialStep = existingData.tutorialStep;
      this.isMuted = existingData.isMuted;

      AIRPORT_LIST.forEach((airportData, index) => {
        const savedAirport = existingData!.airports.find((a) => a.slug === airportData.slug);
        if (savedAirport) {
          const airport = new Airport(index, airportData.name, airportData.slug, airportData.pos);
          airport.isBought = savedAirport.isBought;
          airport.upgrades = savedAirport.upgrades;
          this.airports.push(airport);
        } else {
          // airport may have changed in the meanwhile, perhaps removed from game
          this.airports.push(new Airport(index, airportData.name, airportData.slug, airportData.pos));
        }
      });
      existingData!.airplanes.forEach((a) => {
        const airplane = new Airplane(a.id, a.name, a.airportSlug, a.priceStep);
        airplane.upgrades = a.upgrades;
        airplane.color = a.color;
        this.airplanes.push(airplane);
      });
      existingData!.managers.forEach((m) => {
        const manager = new Manager(m.airportSlug, m.priceStep);
        manager.upgrades = m.upgrades;
        this.managers.push(manager);
      });
      existingData!.lounges.forEach((l) => {
        const lounge = new Lounge(l.airportSlug, l.priceStep);
        lounge.upgrades = l.upgrades;
        this.lounges.push(lounge);
      });
      this.sortStuff();

      if (window.location.href.includes('time_jump=true')) {
        existingData.lastOnline = subHours(new Date(), MAX_OFFLINE_HOURS).toISOString();
      }

      OFFLINE_INCOME.calculate(new Date(existingData.lastOnline));
    } else {
      LOGGER.log('No data, new game init');
      // new init for new player
      DATA.loadDefaultData();
    }
    setAudioMute();
  }

  save() {
    const saveData: SaveData = {
      advanceLevel: this.advanceLevel,
      money: this.money,
      transportedPassangers: this.transportedPassangers,
      lastOnline: new Date().toISOString(),
      tutorialStep: this.tutorialStep,
      isMuted: this.isMuted,
      managers: this.managers.map((m) => ({
        airportSlug: m.airportSlug,
        priceStep: m.priceStep,
        // stringify/parse to get rid of mobx proxy object
        upgrades: JSON.parse(JSON.stringify(m.upgrades)),
      })),
      airports: this.airports.map((a) => ({
        slug: a.slug,
        isBought: a.isBought,
        upgrades: JSON.parse(JSON.stringify(a.upgrades)),
      })),
      airplanes: this.airplanes.map((a) => ({
        id: a.id,
        name: a.name,
        airportSlug: a.airportSlug,
        upgrades: JSON.parse(JSON.stringify(a.upgrades)),
        color: a.color,
        priceStep: a.priceStep,
      })),
      lounges: this.lounges.map((l) => ({
        airportSlug: l.airportSlug,
        upgrades: JSON.parse(JSON.stringify(l.upgrades)),
        priceStep: l.index,
      })),
    };
    CrazySDK.data.setItem(DATA_KEY, JSON.stringify(saveData));
  }

  sortStuff() {
    this.airports.sort((a, b) => a.unlockLevel - b.unlockLevel);
    this.airports.forEach((a) => a.initialFill());
    this.airplanes.sort((a, b) => a.id - b.id);
  }

  airportBySlug(slug: string): Airport {
    return this.airports.find((airport) => airport.slug === slug)!;
  }

  randomAvailableAirport(): Airport {
    const available = this.airports.filter((airport) => airport.isAvailable);
    return available[Math.floor(Math.random() * available.length)];
  }

  airplaneById(id: number): Airplane {
    return this.airplanes.find((a) => a.id === id)!;
  }

  addMoney(value: number) {
    this.money += Math.round(value);
  }

  addTransportedPassangers(value: number) {
    this.transportedPassangers += value;
  }

  addAirplane(airplane: Airplane) {
    this.airplanes.push(airplane);
  }

  addManager(manager: Manager) {
    this.managers.push(manager);
  }

  setOpenPopup(popup: GameData['openPopup'], data: GameData['popupData'] = {}) {
    this.openPopup = popup;
    this.popupData = data;
  }

  setOpenSidebar(popup: GameData['sidebarSection'], data: GameData['sidebarData'] = {}) {
    this.sidebarSection = popup;
    this.sidebarData = data;
  }

  removeCompetitionAirplane(id: string) {
    this.competitionAirplanes = DATA.competitionAirplanes.filter((a) => a.id !== id);
  }

  setTutorialStep(step: GameData['tutorialStep']) {
    this.tutorialStep = step;
  }

  setIsVideoPlaying(value: boolean) {
    this.isVideoPlaying = value;
    setAdMute(value);
  }

  showPlanTripPopup(airportSlug: AirportSlug | null) {
    this.planTripPopup = airportSlug;
  }

  addLounge(lounge: Lounge) {
    this.lounges.push(lounge);
  }

  setBuyMode(mode: GameData['buyMode']) {
    this.buyMode = mode;
  }

  calculateAdvanceData(level: number) {
    const levelForPassengers = level - 1;
    return {
      multiplier: Math.round(Math.pow(ADVANCE_COEFFICIENT, level) * 10) / 10,
      // ADVANCE_COEFFICIENT + 0.2 so that with every advance you will need to transport a little more passengers to advance
      requiredPassengers:
        levelForPassengers < 0 ? 0 : Math.round((10000 * Math.pow(ADVANCE_PASSENGERS_COEFFICIENT, levelForPassengers)) / 100) * 100,
    };
  }

  get currentAdvanceData() {
    return this.calculateAdvanceData(this.advanceLevel);
  }

  get nextAdvanceData() {
    return this.calculateAdvanceData(this.advanceLevel + 1);
  }

  get canAdvance() {
    return this.transportedPassangers >= this.nextAdvanceData.requiredPassengers;
  }

  setIsMute(isMute: boolean) {
    this.isMuted = isMute;
    setAudioMute();
    this.save();
  }

  advance() {
    this.advanceLevel++;
    this.money = 0;
    this.transportedPassangers = 0;
    this.openPopup = null;
    POPUP.clearAll();
    REWARD.endReward();
    REWARD.hidePopup();
    this.loadDefaultData();
    POPUP.showAdvanceCongrats();
    checkManyMoney();
    this.save();
    checkManyMoney();
  }
}

export const DATA = new GameData();
